import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuthContext } from '../auth/AuthContext';
import {
  SUPPRESS_GLOBAL_LOADING_META,
  SUPPRESS_GLOBAL_LOADING_WHEN_CACHED_META,
} from '../context/UiContext';
import { fetchForm } from '../service/form';
import { fetchModerationAccess } from '../screens/ScsModeration/service';
import type { FetchFormResponse } from '../service/types';
import { Query } from '../types';
import { resolveSelectedFormId } from '../utils/form-id';

const getSessionRoleStorageKey = (tenant?: string) => `activeSessionRole:${String(tenant || '').toLowerCase()}`;
const trueLikeValues = new Set(['1', 'true', 'yes', 'y']);
const falseLikeValues = new Set(['0', 'false', 'no', 'n']);
export const REWARD_ADMIN_CHANGE_POLL_INTERVAL_MS = 15 * 1000;
const REWARD_ADMIN_CHANGE_MARKER_PATH = '/runtime/reward-admin-change.json';

const fetchRewardAdminChangeMarker = async (): Promise<string> => {
  const basePath = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  const response = await fetch(`${basePath}${REWARD_ADMIN_CHANGE_MARKER_PATH}`, {
    method: 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error('Unable to check for Reward admin changes');
  }

  const payload: unknown = await response.json();
  const revision =
    payload && typeof payload === 'object'
      ? String((payload as Record<string, unknown>).revision || '').trim()
      : '';
  if (!revision) {
    throw new Error('Invalid Reward admin change marker');
  }
  return revision;
};

const parseOptionalBoolParam = (value: string | null): boolean | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;
  if (trueLikeValues.has(normalized)) return true;
  if (falseLikeValues.has(normalized)) return false;
  return undefined;
};

const normalizeSessionRoles = (response: FetchFormResponse): string => {
  const roleSet = new Set<string>();
  if (Array.isArray(response?.sessionRoles)) {
    response.sessionRoles.forEach((role) => {
      const normalized = String(role || '').trim().toLowerCase();
      if (normalized) roleSet.add(normalized);
    });
  }
  const responseRole = String(response?.role || '').trim().toLowerCase();
  if (responseRole) roleSet.add(responseRole);
  if (Array.isArray(response?.forms)) {
    response.forms.forEach((form) => {
      const normalized = String(form?.role || '').trim().toLowerCase();
      if (normalized) roleSet.add(normalized);
    });
  }
  return Array.from(roleSet).sort().join('|');
};

export const useFormData = () => {
  const { fetchJWT, isAuthenticated } = useAuthContext();
  const { tenant } = useParams<{ tenant: string }>();
  const location = useLocation();

  const pathname = location?.pathname || '';
  const isModerationRoute = tenant === 'scs' && /^\/scs\/moderation\/?$/.test(pathname);
  const logoutParam = new URLSearchParams(location?.search || '').get('logout') || '';
  const isManualLogoutRequested = logoutParam === '1';
  const persistedTenant =
    typeof window !== 'undefined' ? window.localStorage.getItem('currentTenant') || '' : '';
  const urlTenant = String(tenant || '').trim();
  const isTenantSwitchRoute =
    Boolean(persistedTenant) && Boolean(urlTenant) && persistedTenant !== urlTenant;
  const myFormsParam = new URLSearchParams(location?.search || '').get('my_forms') || '';
  const adminViewParam = parseOptionalBoolParam(
    new URLSearchParams(location?.search || '').get('admin'),
  );
  const adminViewQueryKey = adminViewParam === undefined ? '' : adminViewParam ? '1' : '0';
  const selectedFormIdFromContext = resolveSelectedFormId(tenant, location?.search || '');
  const forceMyFormsFromQueryParam =
    typeof myFormsParam === 'string' &&
    trueLikeValues.has(myFormsParam.trim().toLowerCase());
  const shouldRequestMyForms = forceMyFormsFromQueryParam || !selectedFormIdFromContext;
  const selectedFormId = shouldRequestMyForms ? undefined : selectedFormIdFromContext;

  // done redirect to logged out page for these matches:
  // - '/logged-out' or '/<tenant>/logged-out'
  // - '/signin' or '/<tenant>/signin'
  // - '/reset-password' or '/<tenant>/reset-password'
  // - '/reset-password-email' or '/<tenant>/reset-password-email'
  // - '/enter-app-mfa-code' or '/<tenant>/enter-app-mfa-code'
  // - '/set-new-password' or '/<tenant>/set-new-password'
  // - '/<tenant>' (tenant landing page)
  const isOnLoggedOutRoute =
    pathname === '/logged-out' || /^(\/[^/]+)?\/logged-out\/?$/.test(pathname);

  const isOnPublicAuthRoute =
    /^(\/[^/]+)?\/(signin|reset-password|reset-password-email|enter-app-mfa-code|set-new-password)\/?$/.test(
      pathname,
    );

  const redirectToSigninWithSessionExpired = () => {
    // avoid loops and avoid redirecting from public/tenant login routes
    if (isOnLoggedOutRoute || isOnPublicAuthRoute) return;
    const target = isTenantSwitchRoute
      ? tenant
        ? `/${tenant}/signin?ncode=4`
        : '/error?ncode=4'
      : isManualLogoutRequested
      ? tenant
        ? `/${tenant}/signin?loggedOut=1`
        : '/error?loggedOut=1'
      : tenant
        ? `/${tenant}/signin?ncode=2`
        : '/error?ncode=2';
    window.location.assign(target);
  };

  useEffect(() => {
    if (!isTenantSwitchRoute) return;
    if (isOnLoggedOutRoute || isOnPublicAuthRoute) return;
    const target = tenant ? `/${tenant}/signin?ncode=4` : '/error?ncode=4';
    window.location.assign(target);
  }, [isOnLoggedOutRoute, isOnPublicAuthRoute, isTenantSwitchRoute, tenant]);

  const shouldQuery =
    isAuthenticated &&
    !isOnLoggedOutRoute &&
    !isOnPublicAuthRoute &&
    !isManualLogoutRequested &&
    !isTenantSwitchRoute;

  const shouldPollRewardAdminChanges =
    shouldQuery && tenant === 'reward' && shouldRequestMyForms && adminViewParam === true;
  const rewardAdminChangeMarkerQuery = useQuery<string, Error>({
    queryKey: [Query.RewardAdminChangeMarker],
    queryFn: fetchRewardAdminChangeMarker,
    enabled: shouldPollRewardAdminChanges,
    staleTime: 0,
    retry: false,
    refetchInterval: shouldPollRewardAdminChanges ? REWARD_ADMIN_CHANGE_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: shouldPollRewardAdminChanges,
    meta: SUPPRESS_GLOBAL_LOADING_META,
  });
  const rewardAdminChangeRevisionRef = useRef<string>();

  const formDataQuery = useQuery<FetchFormResponse, Error>({
    queryKey: [
      Query.FormData,
      isModerationRoute ? 'moderation' : shouldRequestMyForms ? 'my_forms' : 'single_form',
      selectedFormId || '',
      adminViewQueryKey,
    ],
    queryFn: async () => {
      const token = (await fetchJWT()).idToken;

      if (!token) {
        // No token → only redirect if not on logged-out or public auth/tenant pages
        redirectToSigninWithSessionExpired();
        throw Error('Not authenticated');
      }

      try {
        if (isModerationRoute) return await fetchModerationAccess(token);
        const myFormsQueryParams: Record<string, string> = { my_forms: 'true' };
        if (adminViewParam !== undefined) {
          myFormsQueryParams.admin = adminViewParam ? 'true' : 'false';
        }
        const singleFormQueryParams: Record<string, string> | undefined = selectedFormId
          ? { form_id: selectedFormId }
          : undefined;
        if (singleFormQueryParams && adminViewParam !== undefined) {
          singleFormQueryParams.admin = adminViewParam ? 'true' : 'false';
        }
        if (
          singleFormQueryParams &&
          trueLikeValues.has(
            (new URLSearchParams(location?.search || '').get('review') || '').trim().toLowerCase(),
          )
        ) {
          singleFormQueryParams.review = 'true';
        }
        const response = await fetchForm(
          token,
          shouldRequestMyForms
            ? myFormsQueryParams
            : selectedFormId
              ? singleFormQueryParams
              : undefined,
        );
        const responseRole = String(response.role || '').toLowerCase();
        let editableResponse = response;
        if (
          !shouldRequestMyForms &&
          tenant === 'scs' &&
          response.formState === 'approval' &&
          ['owner', 'editor'].includes(responseRole) &&
          Boolean(response.pendingApprovalQuarter)
        ) {
          editableResponse = { ...response, formState: 'active' };
        }

        // If your fetchForm returns a Response-like object with status
        if ((editableResponse as any)?.status === 401) {
          redirectToSigninWithSessionExpired();
          throw Error('Unauthorized');
        }

        const normalizedSessionRoleSet = normalizeSessionRoles(editableResponse);
        // Only persist/compare session-wide roles from my_forms responses.
        // Single-form responses can legitimately narrow role context (for example owner/editor)
        // and should not overwrite or invalidate tenant session role state used by top-nav tabs.
        if (shouldRequestMyForms) {
          const roleStorageKey = getSessionRoleStorageKey(tenant);
          if (normalizedSessionRoleSet) {
            window.sessionStorage.setItem(roleStorageKey, normalizedSessionRoleSet);
          } else {
            window.sessionStorage.removeItem(roleStorageKey);
          }
        }

        return editableResponse;
      } catch (err: any) {
        const status = err?.status ?? (err?.response && err.response.status);
        const msg = String(err?.message || '');

        if (status === 401 || /401|unauthor/i.test(msg) || /not authenticated/i.test(msg)) {
          redirectToSigninWithSessionExpired();
        }

        throw err;
      }
    },
    enabled:
      shouldQuery && (!shouldPollRewardAdminChanges || rewardAdminChangeMarkerQuery.isFetched),
    staleTime: 0,
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: shouldPollRewardAdminChanges ? REWARD_ADMIN_CHANGE_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
    meta: shouldPollRewardAdminChanges ? SUPPRESS_GLOBAL_LOADING_WHEN_CACHED_META : undefined,
  });
  const refetchFormData = formDataQuery.refetch;

  useEffect(() => {
    if (!shouldPollRewardAdminChanges) {
      rewardAdminChangeRevisionRef.current = undefined;
      return;
    }

    const revision = rewardAdminChangeMarkerQuery.data;
    if (!revision) return;
    if (rewardAdminChangeRevisionRef.current === undefined) {
      rewardAdminChangeRevisionRef.current = revision;
      return;
    }
    if (rewardAdminChangeRevisionRef.current === revision) return;

    rewardAdminChangeRevisionRef.current = revision;
    void refetchFormData();
  }, [refetchFormData, rewardAdminChangeMarkerQuery.data, shouldPollRewardAdminChanges]);

  return formDataQuery;
};
